/**
 * Unassigned, inside Manifest Review (Isaiah Cotto, 2026-09-30), as a board
 * (2026-10-07): the same table, cells and docked record as Scheduling,
 * Finance and the UMED board.
 *
 * The manager's intake queue used to be a tab in the Patient Advocate
 * workspace. The people who place work -- April, Dominique, Kaleigh, Gwynn --
 * do it here, beside Leads to place and Facility coverage.
 *
 * Nothing about the work changed: the same routes (/advocate/unassigned,
 * /advocate/assignments/bulk, /advocate/cases/:id/assign,
 * /manifest/cases/:id/disposition), the same server gate
 * (ADVOCACY_TEAM_MANAGE), the same predicate and count. What changed is the
 * shape: one table row per resident (R7), and the row opens the case DOCKED
 * beside the table -- the same case panel the advocate workspace uses, where
 * the case is assigned -- with Disposition… and the resident's earlier cases
 * under it. Nothing grows inside the table.
 *
 * The URL is the state: ?view=unassigned&q=&lens=&reason=&page=&case=.
 *
 * Bulk acts stay above the table: select / preview / assign many to one
 * advocate (EP-004P), and the bulk send-back (D-28).
 *
 * A resident eMerge holds no name for is a real resident whose name has not
 * arrived from CounselEAR (nameFill.js); the row says "Name not recorded" in
 * words, with a marker, and stays in the queue (the queue's predicate does not
 * read the name; whether it should is a ruling, not this screen's call). A
 * duplicate closed earlier is history, never a raw id: "Duplicate of <name,
 * building>", linked.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  advocateApi, freshKey, isIntakeLens, shortDate, INTAKE_LABEL, INTAKE_LENSES, INTAKE_REASON_LABEL,
  Advocate, BulkPreview, BulkResult, CaseDetail, IntakeLens, Scope, UnassignedCase, UnassignedPage,
} from '../advocate/advocateApi';
import { AdvocateSelect, CaseContext, ExportButton, Refusal, Skeleton, StateBlock, UNREACHED, failureFor, refusalFor, stateLine } from '../advocate/AdvocateShell';
import { StateBlock as TideState } from '../../components/tide';
import Pager from '../../components/board/Pager';
import { DispositionAction } from './ResubmittalsDesk';
import BulkDisposition from './BulkDisposition';
import { verificationApi } from './verificationApi';
import BuildingOrClinic, { buildingWords } from '../../components/emerge/BuildingOrClinic';
import MfBoard, { type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { ResidentName, residentLabel, closedWords, DuplicateOf } from './board/residents';
import { ToneChip, WordChip, useWordColour, type Tone } from './board/chips';
import '../advocate/advocate.css';

const OFFLINE: Refusal = UNREACHED;

export type UnassignedReason = 'NONE' | 'UNRESOLVED_MONDAY_USER' | 'BOARD_PA_NOT_COPIED' | 'UNLINKED' | 'HELD' | 'NOT_A_LEAD' | 'CLOSED_LEADS' | 'STALE_REVIEW' | 'BLOCKED' | '';
const REASON_OPTION: Record<Exclude<UnassignedReason, ''>, string> = {
  NONE: 'Nobody assigned', UNRESOLVED_MONDAY_USER: 'Held by a former board account', BOARD_PA_NOT_COPIED: 'The board names a PA; not copied', UNLINKED: 'Cannot resolve the resident (exceptions)',
  HELD: 'Resident already has an advocate', NOT_A_LEAD: 'Not a lead per CounselEAR',
  CLOSED_LEADS: 'Closed leads (already closed by the board)', STALE_REVIEW: 'Stale review (no recent TWL date)',
  BLOCKED: 'Blocked by eligibility',
};
export const isUnassignedReason = (v: string | null): v is Exclude<UnassignedReason, ''> => v !== null && v in REASON_OPTION;

/** How many residents a list beside the queue holds, as the server counted it; null when it said nothing. */
function countOf(data: UnassignedPage, k: UnassignedReason): number | null {
  const n = k === 'UNLINKED' ? data.unlinked : k === 'HELD' ? data.held : k === 'NOT_A_LEAD' ? data.not_a_lead
    : k === 'CLOSED_LEADS' ? data.closed_leads : k === 'STALE_REVIEW' ? data.stale_review : k === 'BLOCKED' ? data.blocked : undefined;
  return typeof n === 'number' ? n : null;
}

/**
 * Queue health (2026-10-26): every list an open, nobody's case can be in, side
 * by side, each with its count and one line of what it means. Each is a button
 * that opens that list, the same as the Show filter. The counts add up to every
 * open unassigned resident (one list per case; the server's conservation test).
 */
type HealthKey = 'unassigned' | 'closed_leads' | 'stale_review' | 'not_a_lead' | 'held' | 'unlinked' | 'blocked';
const HEALTH: Array<{ reason: UnassignedReason | 'BLOCKED'; key: HealthKey; label: string; what: (days: number) => string }> = [
  { reason: '', key: 'unassigned', label: 'Unassigned now', what: () => 'Leads waiting for an advocate.' },
  { reason: 'CLOSED_LEADS', key: 'closed_leads', label: 'Closed leads', what: () => 'Already closed by the board.' },
  { reason: 'STALE_REVIEW', key: 'stale_review', label: 'Stale review', what: (d) => `No TWL date in the last ${d} days: review, nothing is lost.` },
  { reason: 'NOT_A_LEAD', key: 'not_a_lead', label: 'Not a lead', what: () => 'CounselEAR or eMerge says not a lead.' },
  { reason: 'HELD', key: 'held', label: 'Held', what: () => 'The resident already has an advocate.' },
  { reason: 'UNLINKED', key: 'unlinked', label: 'Unlinked', what: () => 'No resident linked yet.' },
  // Enforce mode only: a BLOCKING intake exception keeps these out of the queue, and no other tile counts them.
  { reason: 'BLOCKED', key: 'blocked', label: 'Blocked by eligibility (enforce mode)', what: () => 'Something must be resolved first. Opens every blocked case.' },
];

function QueueHealth({ data, current, onPick }: { data: UnassignedPage; current: UnassignedReason; onPick: (r: UnassignedReason) => void }) {
  const h = data.queue_health;
  if (!h) return null;
  const days = data.stale_review_days || 30;
  // "Unassigned now" is the queue itself: pressed whenever the view is the queue (with or without a board-reason filter).
  const isQueueView = !current || current === 'NONE' || current === 'UNRESOLVED_MONDAY_USER' || current === 'BOARD_PA_NOT_COPIED';
  const tiles = HEALTH.filter((x) => x.key !== 'blocked' || typeof h.blocked === 'number');
  const total = h.cases ? h.cases.total : null;
  return (
    <div className="mf-health-wrap">
      <div className="mf-health" role="group" aria-label="Queue health">
        {tiles.map((x) => {
          const n = Number(h[x.key] || 0);
          const pressed = x.reason === '' ? isQueueView : String(current) === x.reason;
          return (
            <button key={x.key} type="button" className={`mf-health__item${pressed ? ' is-on' : ''}`} aria-pressed={pressed}
              onClick={() => onPick(x.reason as UnassignedReason)} aria-label={`${x.label}: ${n}. ${x.what(days)}`}>
              <span className="mf-health__n">{n}</span>
              <span className="mf-health__label">{x.label}</span>
              <span className="mf-health__what">{x.what(days)}</span>
            </button>
          );
        })}
      </div>
      {total !== null && (
        <p className="board-src mf-health__total">
          {total} open case{total === 1 ? '' : 's'} nobody holds; each is in exactly one of these lists. The tiles count residents.
        </p>
      )}
    </div>
  );
}
export type BulkFilter = { case_ids?: string[]; q?: string | null; lens?: IntakeLens | null; reason?: string | null; monday_user_id?: string | null };

/** The CounselEAR verdict's tone (2026-10-01), eMerge's own: a lead is green, something to look at amber, not a lead red. */
const VERDICT_TONE: Record<string, Tone> = {
  LEAD: 'ok', LEAD_PENDING: 'ok',
  TWL_NOT_QUALIFIED: 'attn', QUALIFIED_NO_TWL: 'attn', NO_CE_RECORD: 'attn', UNLINKED: 'attn',
  DECEASED: 'stop', INACTIVE: 'stop', HOSPICE: 'stop', HAS_AIDS: 'stop', NOT_QUALIFIED: 'stop', NO_LEAD_TAGS: 'stop',
};

/** "Older than" for a bulk send-back: by the date the case was last placed, else opened. */
const OLDER_THAN: Array<{ days: number | null; label: string }> = [
  { days: null, label: 'Any age' }, { days: 45, label: '45 days' }, { days: 182, label: '6 months' }, { days: 365, label: '1 year' },
];

type Loc = { q: string; lens: IntakeLens; reason: UnassignedReason; page: number; caseId: string | null };

/** Why the resident is here, in words: the intake, the reason, and where it came from. */
function whyHere(c: UnassignedCase): string {
  // 2026-10-05: a resident already sold is not "nobody assigned on the board"; say what the company already did.
  if (c.episode && c.episode.line) return [INTAKE_LABEL[c.intake_kind], c.episode.line].filter(Boolean).join(' · ');
  const reasonWord = c.intake_reason ? INTAKE_REASON_LABEL[c.intake_reason] : null;
  const src = c.source_record_type === 'patients' ? 'from the resident record'
    : c.source_record_type === 'unassigned_twls' ? 'TWL, no lead yet'
      : (c.source_assignee_monday_user_id
        // 2026-10-11: the board's PA is history, never copied. Say whether it is a current eMerge user.
        ? (c.board_pa_resolves ? 'the board names a PA (not copied) — place it' : 'held by a former board account')
        : 'nobody assigned on the board');
  return [INTAKE_LABEL[c.intake_kind], reasonWord, src].filter(Boolean).join(' · ');
}

/** "assigned Jan 5, 2026 · closed Mar 2, 2026 (DNQ)": the resident's latest other case, in words. A duplicate says so, never its id. */
export function priorCaseLine(c: Pick<UnassignedCase, 'last_assigned_on' | 'last_closed_on' | 'last_closed_word'>): string | null {
  const { word } = closedWords(c.last_closed_word);
  const parts = [
    c.last_assigned_on ? `assigned ${shortDate(c.last_assigned_on)}` : null,
    c.last_closed_on ? `closed ${shortDate(c.last_closed_on)}${word ? ` (${word})` : ''}` : null,
  ].filter(Boolean);
  return parts.length ? `Last worked: ${parts.join(' · ')}` : null;
}

export type QuickAssignOutcome = { ok: true; name: string } | { ok: false; message: string };

/**
 * One case, one advocate, one click (EP-007, Dominique Genua: "needs a better
 * way to assign"), as the row carried it before 2026-10-07 -- now in the
 * docked record. One idempotency key per attempt: a retry of a failed attempt
 * sends the SAME key, and only a success starts a new one. Expects nobody to
 * hold the case (`expected_from: null`): a placement made since the list
 * loaded is refused by the server and said in words, never overwritten. The
 * success is said at the top of the board ("Assigned to <name>."), because
 * the placed resident leaves the queue and the record closes with it.
 */
function QuickAssign({ c, advocates, label, onAssign }: { c: UnassignedCase; advocates: Advocate[]; label: string; onAssign: (caseId: string, advocateUserId: string, key: string) => Promise<QuickAssignOutcome> }) {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState(freshKey());
  const go = async () => {
    if (!to || busy) return;
    setBusy(true); setErr(null);
    const out = await onAssign(c.id, to, key);
    setBusy(false);
    if (out.ok) { setTo(''); setKey(freshKey()); } else setErr(out.message);
  };
  return (
    <div className="mf-panel">
      <p>Place this resident with one advocate. Placing it places every open record of theirs.</p>
      <div className="mf-acts">
        <AdvocateSelect advocates={advocates} value={to} onChange={(v) => { setTo(v); setErr(null); }} label={`Assign ${label} to`} />
        <button type="button" className="board-btn board-btn--primary" disabled={!to || busy} onClick={go} aria-label={`Assign ${label}`}>Assign</button>
      </div>
      {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
    </div>
  );
}

function StatusCell({ c, sample = false }: { c: UnassignedCase; sample?: boolean }) {
  // The board word on its Monday colour, the word always on the chip; the case's eMerge stage in words under it.
  const stage = stateLine({ stage: c.stage, disposition: null, lifecycle: null, blockers: c.blockers });
  const hit = useWordColour(c.source_status_raw);
  const chip = hit
    ? <WordChip word={c.source_status_raw} title={`${hit.word}: ${hit.label}`} />
    : <ToneChip tone="quiet" title={c.source_status_raw ? 'eMerge holds no Monday colour for this word' : 'The case carries no board word'}>{c.source_status_raw || 'No board word'}</ToneChip>;
  if (sample) return chip;
  // 2026-10-26: the board closed this lead, and a newer signal (CounselEAR re-tag, provider TWL Q, new TWL date) brought it back.
  const back = c.reopened_signal ? <ToneChip tone="attn" title="The board closed this lead; a TWL signal newer than that closing brought it back to the queue.">Back: new TWL signal since closed</ToneChip> : null;
  return <span className="mf-cellstack">{chip}{back}<span className="board-src">{stage}</span></span>;
}

function VerdictChip({ c }: { c: UnassignedCase }) {
  // M2 (2026-10-07): eMerge's own record of a death keeps the row out of the queue; said in words, never colour alone.
  // #367 round 2: Monday's word alone (the status column, a board lifecycle) is shown, and hides nothing.
  const deceased = c.resident_deceased ? <ToneChip tone="stop">Deceased in eMerge</ToneChip>
    : c.monday_says_deceased ? <ToneChip tone="attn">Monday says deceased</ToneChip> : null;
  if (!c.ce_verdict_words) return deceased || <span className="board-na">not read</span>;
  const verdict = <ToneChip tone={VERDICT_TONE[c.ce_verdict || ''] || 'quiet'}>{c.ce_verdict_words}</ToneChip>;
  return deceased && c.ce_verdict !== 'DECEASED' ? <span className="mf-chips">{deceased}{verdict}</span> : verdict;
}

/** M3 + #367 round 2 (P3-d): the building, whose it is when it is the resident's, and "(unconfirmed)" while FACILITY_UNRESOLVED applies. */
function BuildingCell({ c }: { c: UnassignedCase }) {
  // 2026-10-14: no building -> the resident's CounselEAR clinic, labelled not confirmed; "not recorded" only when CounselEAR names none.
  if (!c.facility_name) return <BuildingOrClinic row={c} fallback={<span className="board-na">not recorded</span>} />;
  const unconfirmed = Array.isArray(c.intake_exceptions) && c.intake_exceptions.includes('FACILITY_UNRESOLVED');
  const note = c.facility_from_resident ? `the resident's building${unconfirmed ? ' (unconfirmed)' : ''}` : unconfirmed ? '(unconfirmed)' : null;
  const name = <span className="board-clip" title={c.facility_name}>{c.facility_name}</span>;
  return note ? <span className="mf-cellstack">{name}<span className="board-src">{note}</span></span> : name;
}

export default function UnassignedDesk({ scope, onScopeStale }: { scope: Scope; onScopeStale: () => void }) {
  const [params, setParams] = useSearchParams();
  const rawLens = params.get('lens');
  const rawReason = params.get('reason');
  const loc: Loc = {
    q: params.get('q') || '',
    lens: isIntakeLens(rawLens) ? rawLens : 'all',
    reason: isUnassignedReason(rawReason) ? rawReason : '',
    page: Math.max(parseInt(params.get('page') || '1', 10) || 1, 1),
    caseId: params.get('case'),
  };
  const { q, lens, reason, page, caseId } = loc;

  // One URL per change, so Back walks the queue the way it always did.
  const go = (patch: Partial<Loc>) => {
    const l = { ...loc, ...patch };
    const next = new URLSearchParams({ view: 'unassigned' });
    if (l.q) next.set('q', l.q);
    if (l.lens !== 'all') next.set('lens', l.lens);
    if (l.reason) next.set('reason', l.reason);
    if (l.page > 1) next.set('page', String(l.page));
    if (l.caseId) next.set('case', l.caseId);
    setParams(next);
  };

  const [data, setData] = useState<UnassignedPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [advocates, setAdvocates] = useState<Advocate[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState(q);
  useEffect(() => { setDraft(q); }, [q]);

  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [detailRefusal, setDetailRefusal] = useState<Refusal | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Bulk (EP-004P, D-28).
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [to, setTo] = useState('');
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [batchKey, setBatchKey] = useState<string>(freshKey());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [olderThan, setOlderThan] = useState<number | null>(null);
  const [olderTotal, setOlderTotal] = useState<number | null>(null);
  const [legendWord, setLegendWord] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true); setRefusal(null);
    try {
      const r = await advocateApi.unassigned({ q, page, lens, reason: reason || null });
      if (r.success && r.data) setData(r.data); else setRefusal(refusalFor(r.code, r.status, r.message));
    } catch { setRefusal(OFFLINE); } finally { setLoading(false); }
  }, [q, page, lens, reason]);
  useEffect(() => { loadList(); }, [loadList]);
  // A new list (search, lens, reason, page) starts with nothing picked.
  useEffect(() => { setPicked(new Set()); setAllMatching(false); setPreview(null); setLegendWord(null); }, [q, page, lens, reason]);

  useEffect(() => {
    advocateApi.advocates().then((r) => { if (r.success && r.data) setAdvocates(r.data); }).catch(() => undefined);
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true); setDetailRefusal(null);
    try {
      const r = await advocateApi.case(id);
      if (r.success && r.data) setDetail(r.data);
      else {
        setDetail(null);
        setDetailRefusal(r.code === 'NOT_FOUND'
          ? { title: 'No such case', body: 'This case does not exist or was closed and removed from the queue.' }
          : failureFor(r) || { title: 'This case is not in your authorized scope', body: 'eMerge holds no patient relationship between you and this resident.', detail: `Decision: ${r.code || 'DENIED'}` });
      }
    } catch { setDetail(null); setDetailRefusal(OFFLINE); } finally { setDetailLoading(false); }
  }, []);
  useEffect(() => { setDetail(null); setDetailRefusal(null); if (caseId) loadDetail(caseId); }, [caseId, loadDetail]);

  const refresh = () => Promise.all([loadList(), caseId ? loadDetail(caseId) : Promise.resolve()]).then(onScopeStale);

  // The case panel's assign: `expectedFrom` is the holder it showed, so a placement made since is refused, never overwritten.
  const onAssign = async (advocateUserId: string | null, note: string, expectedFrom: string | null): Promise<boolean> => {
    if (!caseId) return false;
    setNotice(null);
    try {
      const r = await advocateApi.assign(caseId, advocateUserId, note || undefined, expectedFrom, freshKey());
      if (!r.success) {
        setNotice(r.code === 'ASSIGNMENT_CONFLICT' ? 'Someone else placed this case since you opened it. It was not overwritten — reload to see who holds it.' : (r.error || r.message || 'The server refused this assignment.'));
        if (r.code === 'ASSIGNMENT_CONFLICT') await loadDetail(caseId);
        return false;
      }
      setNotice(advocateUserId ? 'Assigned.' : 'Taken back.');
      await refresh();
      return true;
    } catch { setNotice('Could not reach the server. Nothing was changed.'); return false; }
  };

  // EP-007: one case placed from the record's Assign section. `expected_from: null`: refused if placed since the list loaded.
  const onQuickAssign = async (id: string, advocateUserId: string, key: string): Promise<QuickAssignOutcome> => {
    setNotice(null);
    try {
      const r = await advocateApi.assign(id, advocateUserId, undefined, null, key);
      if (!r.success) {
        return { ok: false, message: r.code === 'ASSIGNMENT_CONFLICT' ? 'Placed by someone else since you looked. Reload to see who holds it.' : (r.error || r.message || 'The server refused this assignment.') };
      }
      const name = advocates.find((a) => a.id === advocateUserId)?.name || 'the advocate';
      await refresh();
      setNotice(`Assigned to ${name}.`);
      return { ok: true, name };
    } catch { return { ok: false, message: 'Could not reach the server. Nothing was changed.' }; }
  };

  const exceptions = data?.reason === 'UNLINKED';
  const heldView = data?.reason === 'HELD';
  const notLeadView = data?.reason === 'NOT_A_LEAD';
  const closedView = data?.reason === 'CLOSED_LEADS';
  const staleView = data?.reason === 'STALE_REVIEW';
  const staleDays = data?.stale_review_days || 30;
  // Bulk Assign places what is in the queue; from a triage list one resident is placed from the record (the send-back still works).
  const triageView = closedView || staleView;
  const items = useMemo(() => data?.items || [], [data]);
  const filter = (): BulkFilter => (allMatching
    ? { q: q || null, lens, reason: reason && reason !== 'UNLINKED' ? reason : null }
    : { case_ids: Array.from(picked) });
  const selectedCount = allMatching ? (data?.total ?? 0) : picked.size;
  const olderLabel = OLDER_THAN.find((o) => o.days === olderThan)?.label || '';
  // With "older than" set, "All N matching" is a different, smaller set: ask the server for its real size.
  useEffect(() => {
    setOlderTotal(null);
    if (!allMatching || olderThan === null) return undefined;
    let live = true;
    verificationApi.selection({ q: q || null, lens, reason: reason && reason !== 'UNLINKED' ? reason : null, older_than_days: olderThan })
      .then((r) => { if (live && r.success && r.data) setOlderTotal(r.data.total); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [allMatching, olderThan, q, lens, reason]);
  const bulkCount = allMatching ? (olderThan === null ? (data?.total ?? 0) : (olderTotal ?? 0)) : picked.size;
  const canBulkClose = !exceptions && !heldView;
  const toggle = (id: string) => { setPreview(null); setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };

  const run = async (dryRun: boolean) => {
    if (!to || selectedCount === 0) return;
    setBusy(true); setDone(null); setErr(null); setNotice(null);
    let out: BulkPreview | BulkResult | null = null;
    try {
      const r = await advocateApi.bulkAssign({ ...filter(), advocate_user_id: to, dry_run: dryRun, idempotency_key: batchKey });
      if (r.success && r.data) out = r.data; else setNotice(r.error || r.message || 'The server refused this assignment.');
    } catch { setNotice('Could not reach the server. Nothing was changed.'); }
    setBusy(false);
    if (!out) { setErr('The server refused this assignment.'); return; }
    if (out.dryRun) { setPreview(out); return; }
    await refresh();
    const parts = [`${out.assigned} assigned`];
    if (out.skipped) parts.push(`${out.skipped} already held by that advocate`);
    if (out.replayed) parts.push(`${out.replayed} already done`);
    if (out.conflicted) parts.push(`${out.conflicted} placed by someone else since your preview — not overwritten`);
    if (out.notFound) parts.push(`${out.notFound} no longer exist`);
    // 2026-10-30: a case whose resident is outside the caller's team scope is never placed; said, not dropped.
    if (out.outOfScope) parts.push(`${out.outOfScope} outside your team scope — not assigned`);
    setDone(`${parts.join(' · ')}.`);
    setPreview(null); setPicked(new Set()); setAllMatching(false); setTo(''); setBatchKey(freshKey());
  };

  // The colour key: the board words drawn on this page, each with how many rows of the page carry it. A filter of this page.
  const legendItems: MfLegendItem[] = useMemo(() => {
    const by = new Map<string, { n: number; sample: UnassignedCase }>();
    for (const c of items) {
      const k = String(c.source_status_raw || '').trim().toUpperCase() || '—';
      const e = by.get(k);
      if (e) e.n += 1; else by.set(k, { n: 1, sample: c });
    }
    return Array.from(by.entries()).map(([k, { n, sample }]) => ({
      key: k,
      label: k === '—' ? 'No board word' : k,
      sample: <StatusCell c={sample} sample />,
      meaning: k === '—' ? 'The case carries no board status word.' : `The Patient Advocate board's word ${k}, filled with its Monday colour.`,
      count: n,
    }));
  }, [items]);
  const shown = legendWord ? items.filter((c) => (String(c.source_status_raw || '').trim().toUpperCase() || '—') === legendWord) : items;

  const columns: BoardColumn<UnassignedCase>[] = [
    ...(!exceptions ? [{
      key: 'pick', label: 'Select', help: 'Pick residents for Preview / Assign or the send-back above the table.',
      render: (c: UnassignedCase) => <input type="checkbox" checked={allMatching || picked.has(c.id)} disabled={allMatching} onChange={() => toggle(c.id)} aria-label={`Select ${residentLabel(c)}`} />,
    }] : []),
    { key: 'facility', label: 'Building', help: 'The building on the case; when the case records none, the resident\'s building, said as such. "(unconfirmed)" while the resident\'s building is not confirmed (FACILITY_UNRESOLVED).', render: (c) => <BuildingCell c={c} /> },
    { key: 'status', label: 'Status', help: 'The case\'s board word, filled with its Monday colour, and the case\'s eMerge stage under it.', render: (c) => <StatusCell c={c} /> },
    { key: 'why', label: 'Why it is here', help: 'The intake, its reason and where it came from; for a resident already sold, what the company already did.', render: (c) => <span className="board-clip" title={whyHere(c)}>{whyHere(c)}</span> },
    { key: 'ce', label: 'CounselEAR', help: 'What CounselEAR says about the resident (eMerge\'s reading of its tags). Green: a lead. Amber: look first. Red: not a lead.', render: (c) => <VerdictChip c={c} /> },
    { key: 'qual', label: 'Resident status', help: 'The resident\'s qualification status as eMerge holds it.', render: (c) => (c.qualification_status && c.qualification_status.length ? <span className="mf-chips">{c.qualification_status.map((w) => <ToneChip key={w} tone="quiet">{w}</ToneChip>)}</span> : <span className="board-na">not recorded</span>) },
    { key: 'twl', label: 'TWL date', help: 'The TWL Date, the business clock. When the source recorded none, the day eMerge first saw the case, said as such.', render: (c) => (c.twl_effective_at ? <span>{shortDate(c.twl_effective_at)}</span> : <span className="mf-cellstack"><span className="board-na">not recorded</span><span className="board-src">first seen {shortDate(c.intake_observed_at)}</span></span>) },
    { key: 'records', label: 'Open records', numeric: true, help: 'How many open records this resident has in this view (R7). Placing the row places them all.', render: (c) => <span>{c.open_records || 1}</span> },
    { key: 'last', label: 'Last worked', help: 'The resident\'s most recent other case: when it was assigned and when it closed, and with what word.', render: (c) => { const l = priorCaseLine(c); return l ? <span className="board-clip" title={l}>{l.replace(/^Last worked: /, '')}</span> : <span className="board-na">no earlier case</span>; } },
  ];

  const identity = {
    label: 'Resident',
    help: 'The resident nobody holds. Open the row for the case, docked beside the table: assign it there, close it with a disposition, or read their earlier cases.',
    render: (c: UnassignedCase) => <ResidentName p={c} />,
    name: (c: UnassignedCase) => residentLabel(c),
  };

  const sections = (c: UnassignedCase): BoardDetailSection[] => {
    const prior = closedWords(c.last_closed_word);
    const out: BoardDetailSection[] = [];
    if (!exceptions) out.push({ id: 'assign', label: 'Assign', render: () => <QuickAssign key={c.id} c={c} advocates={advocates} label={residentLabel(c)} onAssign={onQuickAssign} /> });
    out.push({
      id: 'case', label: 'Case and assignment',
      render: () => (
        <div className="pa">
          <CaseContext caseId={caseId} detail={detail} refusal={detailRefusal} loading={detailLoading} manager={scope.manager} advocates={advocates} onAssign={onAssign}
            scope={scope} onChanged={async (msg) => { setNotice(msg); await refresh(); }} docked />
        </div>
      ),
    });
    if (!heldView && !String(c.intake_kind || '').startsWith('REPLACEMENT')) {
      out.push({
        id: 'disposition', label: 'Close instead',
        render: () => (
          <div className="mf-panel">
            <p>Close this lead with a disposition and a reason instead of placing it. It leaves the queue and stays findable in History and resident search.</p>
            <DispositionAction caseId={c.id} residentLabel={residentLabel(c)} onDone={async (m) => { setNotice(m); await refresh(); }} />
          </div>
        ),
      });
    }
    out.push({
      id: 'history', label: 'Earlier cases',
      render: () => (
        <div className="mf-panel">
          <dl className="mf-facts">
            <dt>Last assigned</dt><dd>{c.last_assigned_on ? shortDate(c.last_assigned_on) : <span className="board-na">never</span>}</dd>
            <dt>Last closed</dt><dd>{c.last_closed_on ? `${shortDate(c.last_closed_on)}${prior.word ? ` (${prior.word})` : ''}` : <span className="board-na">no closed case</span>}</dd>
            {prior.duplicateOf && (<><dt>Closed as</dt><dd><DuplicateOf caseId={prior.duplicateOf} selfId={c.id} selfName={residentLabel(c)} selfFacility={buildingWords(c, '') || null} /></dd></>)}
            <dt>Open records</dt><dd>{c.open_records || 1}{(c.other_open_records || []).length ? ` (also: ${(c.other_open_records || []).map((o) => `${o.stage.toLowerCase().replace(/_/g, ' ')}${o.source_system ? `, ${o.source_system === 'EMERGE' ? 'eMerge' : 'the board'}` : ''}`).join('; ')})` : ''}</dd>
          </dl>
        </div>
      ),
    });
    return out;
  };

  const title = exceptions ? 'Unassigned · exceptions' : heldView ? 'Unassigned · resident already has an advocate' : notLeadView ? 'Unassigned · not a lead per CounselEAR'
    : closedView ? 'Unassigned · closed leads' : staleView ? 'Unassigned · stale review' : data?.reason === 'BLOCKED' ? 'Unassigned · blocked by eligibility' : 'Unassigned';
  const what = exceptions ? 'Open cases eMerge cannot link to a resident. Not assignable until the resident is known.'
    : heldView ? 'A second open row for a resident an advocate already holds. Not new work — the same work, seen twice. Placing it with that advocate brings every row for the resident together.'
      : notLeadView ? 'Open and nobody\'s, but CounselEAR says the resident is inactive, deceased, in hospice, already fitted, did not qualify, or has no lead tag — or eMerge records the resident as deceased. Kept out of the queue and its count. Nothing is closed; if CounselEAR changes, the case returns to the queue.'
        : closedView ? 'Already closed by the board: the lead carries the board\'s closing word (DNQ, NITWL, C-NITWL, private pay, enrolled, or discharged, deceased, hospice, dropped facility). Kept out of the queue and its count. Nothing is closed or changed here; if the resident came back, open the row and place them.'
          : staleView ? `No TWL date in the last ${staleDays} days: review, nothing is lost. No TWL date, first-seen date or CounselEAR TWL tag in that window, and no Manifest Review confirmation. Kept out of the queue and its count, never closed. A CounselEAR re-tag, a provider TWL Q mark or a new TWL date brings the case back to the queue by itself.`
            : `New PA intake waiting for an advocate: work nobody holds, that CounselEAR still says is a lead, with a TWL date in the last ${staleDays} days or a Manifest Review confirmation. Open a row to place it.`;

  const controls = data ? (
    <>
      <QueueHealth data={data} current={reason} onPick={(r) => go({ reason: r, page: 1, caseId: null })} />
      <form className="mf-search" role="search" onSubmit={(e) => { e.preventDefault(); go({ q: draft.trim(), page: 1, caseId: null }); }}>
        <input type="search" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Search by resident or facility" aria-label="Search by resident or facility" />
        <button type="submit" className="board-btn">Search</button>
        {q && <button type="button" className="board-btn" onClick={() => go({ q: '', page: 1, caseId: null })} aria-label="Clear the search">Clear</button>}
      </form>
      {!exceptions && (
        <div className="mf-lenses" role="group" aria-label="Intake view">
          {INTAKE_LENSES.map((l) => (
            <button key={l.key} type="button" className={`board-btn${lens === l.key ? ' board-btn--primary' : ''}`} aria-pressed={lens === l.key} title={l.what} onClick={() => go({ lens: l.key, page: 1, caseId: null })}>
              {l.label}{data.lens_counts && data.lens_counts[l.key] !== undefined ? <span className="mf-lenscount"> {data.lens_counts[l.key]}</span> : null}
            </button>
          ))}
        </div>
      )}
      <label className="mf-check">
        <span>Show</span>
        <select value={reason} onChange={(e) => go({ reason: e.target.value as UnassignedReason, page: 1, caseId: null })} aria-label="Show">
          <option value="">Everything in this view</option>
          {(Object.keys(REASON_OPTION) as Array<Exclude<UnassignedReason, ''>>).map((k) => { const n = countOf(data, k); return <option key={k} value={k}>{REASON_OPTION[k]}{n ? ` (${n})` : ''}</option>; })}
        </select>
      </label>
      <ExportButton what="these leads" run={() => advocateApi.exportUnassigned({ q, lens, reason: reason || null })} />
      {!exceptions && (
        <div className="mf-bulk" aria-label="Act on many">
          <label className="mf-check"><input type="checkbox" checked={allMatching} onChange={(e) => { setAllMatching(e.target.checked); setPreview(null); }} aria-label={`Select all ${data.total} matching`} /> All {data.total} matching</label>
          {triageView && <span className="board-src">To place one of these residents, open the row and use Assign.</span>}
          {!triageView && <AdvocateSelect advocates={advocates} value={to} onChange={(v) => { setTo(v); setPreview(null); }} label="Assign to" />}
          {!triageView && !preview && <button type="button" className="board-btn" disabled={!to || selectedCount === 0 || busy} onClick={() => run(true)}>Preview{selectedCount ? ` ${selectedCount}` : ''}</button>}
          {!triageView && preview && (
            <>
              <span role="status">{preview.matched} case{preview.matched === 1 ? '' : 's'} would be assigned{preview.excluded.not_in_queue ? `; ${preview.excluded.not_in_queue} no longer in the queue and left out` : ''}{preview.outOfScope ? `; ${preview.outOfScope} outside your team scope and left out` : ''}.</span>
              <button type="button" className="board-btn board-btn--primary" disabled={busy || preview.matched === 0} onClick={() => run(false)}>Assign {preview.matched}</button>
              <button type="button" className="board-btn" onClick={() => setPreview(null)}>Cancel</button>
            </>
          )}
          {/* Only with "All N matching": a filter over rows picked by hand would be shown and silently ignored. */}
          {canBulkClose && allMatching && (
            <label className="mf-check">
              Send back only older than{' '}
              <select value={olderThan === null ? '' : String(olderThan)} aria-label="Send back only older than" title="Applies to the send-back below, not to Assign"
                onChange={(e) => { setOlderThan(e.target.value ? Number(e.target.value) : null); setPreview(null); }}>
                {OLDER_THAN.map((o) => <option key={o.label} value={o.days === null ? '' : String(o.days)}>{o.label}</option>)}
              </select>
            </label>
          )}
          {canBulkClose && allMatching && olderThan !== null && <span role="status">{olderTotal === null ? 'Counting…' : `${olderTotal} older than ${olderLabel}`}</span>}
          {done && <span role="status">{done}</span>}
          {err && <span className="mf-notice mf-notice--stop" role="alert">{err}</span>}
        </div>
      )}
      {canBulkClose && (
        <BulkDisposition count={bulkCount}
          resolveIds={async () => {
            if (!allMatching) return Array.from(picked);
            const r = await verificationApi.selection({ q: q || null, lens, reason: reason && reason !== 'UNLINKED' ? reason : null, older_than_days: olderThan });
            if (!r.success || !r.data) throw new Error('selection');
            return r.data.case_ids;
          }}
          labelFor={(id) => { const c = items.find((x) => x.id === id); return c ? residentLabel(c) : undefined; }}
          onClear={() => { setPicked(new Set()); setAllMatching(false); setPreview(null); }}
          onFinished={async (m) => { setPicked(new Set()); setAllMatching(false); setPreview(null); setNotice(m); await refresh(); }} />
      )}
    </>
  ) : null;

  const filtered = Boolean(q) || lens !== 'all' || Boolean(data?.reason);
  let state: React.ReactNode = null;
  if (refusal) state = <StateBlock refusal={refusal} retry={loadList} />;
  else if (loading && !data) state = <Skeleton />;
  else if (data && data.items.length === 0 && triageView && !q && lens === 'all') {
    state = closedView
      ? <TideState kind="empty" reason="none" message="No closed leads are waiting. Nothing the board already closed is sitting unassigned." />
      : <TideState kind="empty" reason="none" message={`Nothing stale. Every unassigned lead has a TWL date in the last ${staleDays} days or a Manifest Review confirmation.`} />;
  } else if (data && data.items.length === 0) {
    state = data.queue_total === 0 && !exceptions && !triageView
      ? <TideState kind="empty" reason="none" kindLabel="Nothing waiting" message="No unassigned PA cases. New eligible intake will appear here automatically." />
      : exceptions && data.unlinked === 0
        ? <TideState kind="empty" reason="none" message="No exceptions. Every unassigned case is linked to a resident." />
        : (
          <div className="mf-panel">
            <p>No unassigned cases match this search or filter. {data.queue_total} unassigned case{data.queue_total === 1 ? '' : 's'} remain.</p>
            <div className="mf-acts">
              {q && <button type="button" className="board-btn" onClick={() => go({ q: '', page: 1, caseId: null })}>Clear search</button>}
              {(lens !== 'all' || data.reason) && <button type="button" className="board-btn" onClick={() => go({ lens: 'all', reason: '', page: 1, caseId: null })}>Clear filters</button>}
            </div>
          </div>
        );
  }

  return (
    <main className="mf-main mf-main--board">
      <MfBoard<UnassignedCase>
        title={title}
        count={data ? data.total : null}
        units={['resident', 'residents']}
        what={what}
        notice={notice ? <p className="mf-notice" role="status">{notice}</p> : null}
        controls={controls}
        legend={data && items.length > 0 ? (
          <MfLegend title="Colour key" items={legendItems} whole={items.length} wholeWords="on this page" units={['row', 'rows']}
            value={legendWord} onSelect={setLegendWord}
            scope="Each chip is the Patient Advocate board's word for the case, filled with its Monday colour; a grey outline means no board word, or no colour held for it. Press an entry to show only those rows of this page." />
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={shown}
        rowKey={(c) => c.id}
        openKey={caseId}
        onOpenChange={(k) => go({ caseId: k })}
        detailSections={sections}
        detailLabel={(c) => `${residentLabel(c)}: the case`}
        pager={data ? <Pager page={data.page} limit={data.limit} total={data.total} onPage={(p) => go({ page: p, caseId: null })} label="residents" /> : null}
        foot={data && filtered && data.items.length > 0 && data.total !== data.queue_total && !exceptions
          ? <p>Showing {data.total} of {data.queue_total} unassigned{q ? ' matching your search' : ''}{lens !== 'all' ? ` · ${INTAKE_LENSES.find((l) => l.key === lens)?.label}` : ''}.</p>
          : null}
        notes={['"Name not recorded": eMerge holds no name for this resident yet; CounselEAR sends the name after the resident. The resident is still a lead.']}
      />
    </main>
  );
}
